import { Collapse, Divider, Stack } from '@mui/material'
import React, { useEffect, useState } from 'react'
import ActuatorOption from '../base/ActuatorOption'
import { FormProvider, useForm } from 'react-hook-form'
import { ActuatorPayload, TimerActuator, TimerActuatorForm } from '../../models/smart-home.model'
import { useTimerActuatorFormInitializer } from '../../hooks/actuator-form-initializer/use-timer-actuator-form-Initializer'
import { zodResolver } from '@hookform/resolvers/zod'
import { TextField } from 'src/Libs/inputs/react-hook-form'
import MultilineActorBase from '../base/MultilineActorBase'
import { BZTime } from '@e3dc-react/icons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { LoadingButton } from '@mui/lab'
import { useActuatorUpdate } from '../../hooks/use-actuator-update'
import { useAutoActuatorHeaderContent } from '../../hooks/use-auto-actuator-header-content'
import ActuatorAutoOption from '../base/ActuatorAutoOption'
import ActuatorRepeatOption from '../base/ActuatorRepeatOption'

interface Props {
  serialNo: number
  actuator: TimerActuator
}

const Timer: React.FC<Props> = ({ serialNo, actuator }) => {
  const { t } = useCustomTranslation()
  const { initialValues, validationSchema } = useTimerActuatorFormInitializer(actuator)
  const [selectedState, setSelectedState] = useState(actuator.state === 'on')
  const [showAutoOptions, setShowAutoOptions] = useState(actuator.descriptions.auto ?? false)
  const [isLoading, setIsLoading] = useState(false)
  const onUpdateActuator = useActuatorUpdate({ setIsLoading })
  const headerContent = useAutoActuatorHeaderContent({
    serialNo,
    actuator,
    showAutoOptions,
    isLoading,
    selectedState,
    setIsLoading,
    setSelectedState,
  })

  const methods = useForm<TimerActuatorForm>({
    defaultValues: initialValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  })

  const autoValue = methods.watch('auto')

  useEffect(() => {
    if (autoValue || (!autoValue && actuator.descriptions.auto)) {
      setShowAutoOptions(true)
    } else {
      setShowAutoOptions(false)
    }
  }, [autoValue, actuator.descriptions.auto])

  const onSubmit = (values: TimerActuatorForm): void => {
    const payload: ActuatorPayload = { command: values.auto ? 'auto' : 'manual', descriptions: { ...values } }
    onUpdateActuator(serialNo, actuator, payload)
  }

  return (
    <FormProvider {...methods}>
      <MultilineActorBase actuator={actuator} headerContent={headerContent}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Stack
            sx={{ backgroundColor: 'background.paper2', borderColor: 'grey.grey06', py: 2, px: 4, mt: 2 }}
            gap={2}
            border={1}
            borderRadius={theme => `${theme.shape.borderRadius}px`}
          >
            <ActuatorAutoOption />
            <Collapse in={showAutoOptions} unmountOnExit>
              <Stack gap={2}>
                <ActuatorOption title={'smart-home.timer.start-time.title'}>
                  <TextField name="timeOn" mask={'##:##'} fullWidth={false} endAdornment={<BZTime />} clearable={false} sx={{ mb: 2 }} />
                </ActuatorOption>
                <ActuatorOption title={'smart-home.timer.end-time.title'}>
                  <TextField name="timeOff" mask={'##:##'} fullWidth={false} endAdornment={<BZTime />} clearable={false} sx={{ mb: 2 }} />
                </ActuatorOption>
                <ActuatorRepeatOption />
                <Divider sx={{ mx: -4, borderColor: 'grey.grey06' }} />
                <Stack direction="row" justifyContent="flex-end" gap={2}>
                  <LoadingButton variant="contained" type="submit" disabled={!methods.formState.isDirty} loading={isLoading}>
                    {t('common.save')}
                  </LoadingButton>
                </Stack>
              </Stack>
            </Collapse>
          </Stack>
        </form>
      </MultilineActorBase>
    </FormProvider>
  )
}

export default Timer
